import { useContext, useEffect, useMemo, useState } from "react";
import { App } from "./App";
import { AuthCtx } from "./AuthGate";
import { AppStore, safeStorage } from "./store";
import { ProgressSync, claimDevice, clearLocal, createApi } from "../lib/sync";

const POLL_MS = 60_000;
const OFFLINE_NOTICE = "Couldn’t reach the server. Progress is saved on this device and will sync when you’re back online.";

/** Check the server on a timer while the tab is visible, when it becomes visible, and when the network returns. */
function startBackgroundSync(sync: ProgressSync): () => void {
  const visible = () => document.visibilityState === "visible";
  const interval = setInterval(() => { if (visible()) void sync.pull(); }, POLL_MS);
  const onVisibility = () => { if (visible()) void sync.pull(); else void sync.flush({ keepalive: true }); };
  const onOnline = () => { void sync.flush().then(() => sync.pull()); };
  const onPageHide = () => { void sync.flush({ keepalive: true }); };
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("online", onOnline);
  window.addEventListener("pagehide", onPageHide);
  return () => {
    clearInterval(interval);
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("online", onOnline);
    window.removeEventListener("pagehide", onPageHide);
  };
}

/** The signed-in app: merges local progress with the server first, then keeps the two in step while it is open. */
export function SyncedApp() {
  const auth = useContext(AuthCtx);
  const [ready, setReady] = useState<{ store: AppStore; sync: ProgressSync | null } | null>(null);

  useEffect(() => {
    if (!auth.userId) return;
    let cancelled = false;
    let stop = () => {};
    const storage = safeStorage();
    (async () => {
      let sync: ProgressSync | null = null;
      let store: AppStore | null = null;
      let online = true;
      if (storage) {
        try {
          claimDevice(storage, auth.userId!);
          sync = new ProgressSync(storage, createApi(auth.getToken), auth.userId!, {
            onOnline: (ok) => {
              if (!store) { online = ok; return; }
              // Show the offline notice while unreachable and clear it once a sync succeeds.
              if (!ok && !store.saveWarning) { store.saveWarning = OFFLINE_NOTICE; store.notify(); }
              else if (ok && store.saveWarning === OFFLINE_NOTICE) { store.saveWarning = null; store.notify(); }
            },
          });
          await sync.pull(); // merge before the app reads storage
        } catch (error) {
          console.warn("progress sync unavailable", error);
          sync = null;
        }
      }
      if (cancelled) return;
      store = new AppStore(storage, (state) => sync?.observe(state as never));
      sync?.attach(store.state as never, (id, data) => store!.applyRemote(id, data));
      if (storage && (!sync || !online)) store.saveWarning = OFFLINE_NOTICE;
      if (sync) stop = startBackgroundSync(sync);
      setReady({ store, sync });
    })();
    return () => { cancelled = true; stop(); };
  }, [auth.userId, auth.getToken]);

  // Sign-out pushes pending changes first, then clears this device so the next person starts clean.
  const signOutAuth = useMemo(() => ({
    ...auth,
    async signOut() {
      await ready?.sync?.flush();
      const storage = safeStorage();
      if (storage && !ready?.sync?.dirty().length) { ready?.store.player.stop(); clearLocal(storage); }
      await auth.signOut();
    },
  }), [auth, ready]);

  if (!ready) return <p className="loading">Getting your progress…</p>;
  return <AuthCtx.Provider value={signOutAuth}><App store={ready.store} /></AuthCtx.Provider>;
}
